import { useId, type ReactNode } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { CredentialCategory } from "../../types";
import {
  ALL_VAULT_CATEGORIES,
  hasVaultFileFilters,
  VaultFileSort,
  VaultFileStatusFilter,
  type VaultFileFilters,
} from "../../lib/vault-file-filters";

const STATUS_LABELS: Record<VaultFileStatusFilter, string> = {
  [VaultFileStatusFilter.All]: "All statuses",
  [VaultFileStatusFilter.Attached]: "Attached",
  [VaultFileStatusFilter.NeedsReview]: "Needs review",
  [VaultFileStatusFilter.Processing]: "Processing",
  [VaultFileStatusFilter.Unavailable]: "Unavailable",
};

function FileFilterSelect({
  label,
  value,
  active = false,
  hideLabel = false,
  onChange,
  children,
}: {
  label: string;
  value: string;
  active?: boolean;
  hideLabel?: boolean;
  onChange: (value: string) => void;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <div className="min-w-0 space-y-1.5">
      <Label
        htmlFor={id}
        className={cn(
          "block text-xs font-semibold text-slate-700",
          hideLabel && "sr-only",
        )}
      >
        {label}
      </Label>
      <div className="relative">
        <select
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={cn(
            "h-11 w-full cursor-pointer appearance-none rounded-xl border border-slate-400 bg-white py-2 pl-3 pr-9 text-sm font-medium text-slate-800 shadow-sm transition-colors hover:border-slate-600 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20",
            active && "border-primary bg-primary/5 text-primary",
            hideLabel && "h-10 text-xs sm:text-sm",
          )}
        >
          {children}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-slate-500"
        />
      </div>
    </div>
  );
}

export function VaultFilesSort({
  value,
  onChange,
}: {
  value: VaultFileSort;
  onChange: (sort: VaultFileSort) => void;
}) {
  return (
    <FileFilterSelect
      label="Sort"
      value={value}
      hideLabel
      onChange={(sort) => onChange(sort as VaultFileSort)}
    >
      <option value={VaultFileSort.Newest}>Newest first</option>
      <option value={VaultFileSort.Oldest}>Oldest first</option>
    </FileFilterSelect>
  );
}

export function VaultFilesToolbar({
  filters,
  categories,
  onChange,
  onClear,
}: {
  filters: VaultFileFilters;
  categories: CredentialCategory[];
  onChange: (patch: Partial<VaultFileFilters>) => void;
  onClear: () => void;
}) {
  const id = useId();
  const activeCategory = categories.find(
    (category) => category.slug === filters.category,
  );
  const hasStatusFilter = filters.status !== VaultFileStatusFilter.All;
  return (
    <section
      aria-labelledby={`${id}-heading`}
      className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
    >
      <div className="flex min-h-5 items-center justify-between gap-2">
        <h3
          id={`${id}-heading`}
          className="text-sm font-semibold text-slate-900"
        >
          Find files
        </h3>
        {hasVaultFileFilters(filters) && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={onClear}
          >
            Clear filters
          </Button>
        )}
      </div>
      <div className="relative">
        <Label htmlFor={`${id}-search`} className="sr-only">
          Search files
        </Label>
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-500"
        />
        <Input
          id={`${id}-search`}
          type="search"
          value={filters.search}
          placeholder="Search filename or credential"
          onChange={(event) => onChange({ search: event.target.value })}
          className="h-11 rounded-xl border-slate-400 bg-white pl-10 text-sm shadow-sm placeholder:text-slate-500"
        />
      </div>
      <div className="grid grid-cols-1 gap-3 min-[360px]:grid-cols-2">
        <FileFilterSelect
          label="Category"
          value={filters.category}
          active={filters.category !== ALL_VAULT_CATEGORIES}
          onChange={(category) => onChange({ category })}
        >
          <option value={ALL_VAULT_CATEGORIES}>All categories</option>
          {categories.map((category) => (
            <option key={category.slug} value={category.slug}>
              {category.label}
            </option>
          ))}
        </FileFilterSelect>
        <FileFilterSelect
          label="Status"
          value={filters.status}
          active={hasStatusFilter}
          onChange={(status) =>
            onChange({ status: status as VaultFileStatusFilter })
          }
        >
          {Object.values(VaultFileStatusFilter).map((status) => (
            <option key={status} value={status}>
              {STATUS_LABELS[status]}
            </option>
          ))}
        </FileFilterSelect>
      </div>
      {(activeCategory || hasStatusFilter) && (
        <div className="flex flex-wrap gap-2" aria-label="Active filters">
          {activeCategory && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 rounded-full border-primary/30 bg-primary/5 px-2.5 text-xs text-primary"
              aria-label={`Remove category filter: ${activeCategory.label}`}
              onClick={() => onChange({ category: ALL_VAULT_CATEGORIES })}
            >
              {activeCategory.label}
              <X className="size-3" aria-hidden="true" />
            </Button>
          )}
          {hasStatusFilter && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 rounded-full border-primary/30 bg-primary/5 px-2.5 text-xs text-primary"
              aria-label={`Remove status filter: ${STATUS_LABELS[filters.status]}`}
              onClick={() => onChange({ status: VaultFileStatusFilter.All })}
            >
              {STATUS_LABELS[filters.status]}
              <X className="size-3" aria-hidden="true" />
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
